import { type FieldMetadata, useInputControl } from "@conform-to/react"
import * as RadixSelect from "@radix-ui/react-select"
import * as LucideIcons from "lucide-react"
import { twMerge } from "tailwind-merge"
import { ChevronLeft } from "./icons/ChevronLeft"

// Popular/commonly used icons for notifications
const POPULAR_ICONS = [
	"Bell",
	"Sparkles",
	"Star",
	"Gift",
	"Trophy",
	"Target",
	"Users",
	"Megaphone",
	"Info",
	"AlertCircle",
	"CheckCircle",
	"XCircle",
	"TrendingUp",
	"Flame",
	"Zap",
	"Heart",
	"Hand",
	"Smartphone",
	"Mail",
	"Calendar",
	"Clock",
	"MapPin",
	"Home",
	"Settings",
	"Search",
] as const

type IconName = (typeof POPULAR_ICONS)[number]

export function IconPicker({ meta, className }: { meta: FieldMetadata<string>; className?: string }) {
	const control = useInputControl(meta)
	const selectedIcon = control.value || meta.initialValue || ""
	const displayValue = selectedIcon || "none"

	return (
		<RadixSelect.Root
			name={meta.name}
			value={displayValue}
			onValueChange={(value) => {
				control.change(value === "none" ? "" : value)
			}}
			onOpenChange={(open) => {
				if (!open) {
					control.blur()
				}
			}}
		>
			<RadixSelect.Trigger
				className={twMerge(
					"border border-foreground/30 bg-transparent [&>span]:px-3 py-2 text-left data-[placeholder]:text-foreground/60",
					"inline-flex items-center justify-between gap-2",
					className,
				)}
			>
				<RadixSelect.Value placeholder="No icon">
					{selectedIcon ? (
						<div className="flex items-center gap-2">
							{(() => {
								const IconComponent = (LucideIcons as any)[selectedIcon as IconName] as
									| React.ComponentType<{ className?: string }>
									| undefined
								if (IconComponent) {
									return <IconComponent className="size-4" />
								}
								return null
							})()}
							<span>{selectedIcon}</span>
						</div>
					) : (
						"No icon"
					)}
				</RadixSelect.Value>
				<RadixSelect.Icon>
					<ChevronLeft className="size-4 -rotate-90" />
				</RadixSelect.Icon>
			</RadixSelect.Trigger>

			<RadixSelect.Portal>
				<RadixSelect.Content
					position="popper"
					className="rounded-sm border border-foreground/60 bg-background w-[var(--radix-select-trigger-width)] z-50 shadow-lg"
					sideOffset={5}
				>
					<RadixSelect.Viewport className="max-h-[300px] overflow-y-auto">
						<RadixSelect.Item
							value="none"
							className="py-1.5 px-3 data-[highlighted]:bg-foreground/10 data-[state=checked]:bg-foreground/10"
						>
							<RadixSelect.ItemText>No icon</RadixSelect.ItemText>
						</RadixSelect.Item>
						{POPULAR_ICONS.map((iconName) => {
							const IconComponent = (LucideIcons as any)[iconName] as
								| React.ComponentType<{ className?: string }>
								| undefined
							if (!IconComponent) return null

							return (
								<RadixSelect.Item
									key={iconName}
									value={iconName}
									className="py-1.5 px-3 data-[highlighted]:bg-foreground/10 data-[state=checked]:bg-foreground/10"
								>
									<RadixSelect.ItemText>
										<div className="flex items-center gap-2">
											<IconComponent className="size-4" />
											<span>{iconName}</span>
										</div>
									</RadixSelect.ItemText>
								</RadixSelect.Item>
							)
						})}
					</RadixSelect.Viewport>
				</RadixSelect.Content>
			</RadixSelect.Portal>
		</RadixSelect.Root>
	)
}

/**
 * Renders a Lucide icon dynamically by name
 */
export function DynamicIcon({
	iconName,
	className,
}: {
	iconName: string | null
	className?: string
}) {
	if (!iconName) return null

	const IconComponent = (LucideIcons as any)[iconName] as
		| React.ComponentType<{ className?: string }>
		| undefined

	if (!IconComponent) return null

	return <IconComponent className={className} />
}

